import React from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/core/styles";
import Slider from "react-slick";
import classNames from "classnames";
import IconButton from "@material-ui/core/IconButton";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import "../../styles/components/vendors/slick-carousel/slick-carousel.css";
import "../../styles/components/vendors/slick-carousel/slick.css";
import "../../styles/components/vendors/slick-carousel/slick-theme.css";
import "../../styles/components/vendors/react-animated-slider/react-animated-slider.css";

const styles = theme => ({
  carouselContainer: {
    width: '100%',
    padding: '0 0 10px',
    '& .slick-slide': {
      padding: '0 12px',
      height: 'inherit',
      '& > div': {
        height: '100%',
        width: '100%',
        display: 'flex',
        '& > div': {
          width: '100%',
          display: 'flex',
          '& > div': {
            width: '100%',
            display: 'flex',
          }
        }
      }
    },
    '& .slick-list': {
      margin: '0 -12px',
    },
    '& .slick-track': {
      display: 'flex',
      '& .slick-slide': {
        display: 'flex',
        height: 'auto',
        alignItems: 'stretch',
      }
    },
    '& .slick-dots': {
      bottom: '0px',
      position: 'relative',
      marginTop: '0px',
      '& li': {
        margin: '0 5px',
      },
      '& li button': {
        width: '12px',
        height: '12px',
        padding: 0,
        '&:before': {
          content: '""',
          width: '12px',
          height: '12px',
          borderRadius: '2px',
          border: `2px solid #ffffff`,
          backgroundColor: 'transparent',
          opacity: 1,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
        }
      },
      '& li.slick-active button:before': {
        backgroundColor: '#ffffff',
      }
    }
  },
  carouselContainerWhiteBg: {
    '& .slick-dots': {
      '& li button:before': {
        borderColor: `${theme.palette.primary.main} !important`,
      },
      '& li.slick-active button:before': {
        backgroundColor: `${theme.palette.primary.main} !important`,
      }
    }
  },
  customArrowsContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '8px',
    marginTop: '0px',
  },
  arrowButton: {
    padding: '4px',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&.Mui-disabled': {
      opacity: 0.3,
    }
  },
  arrowIcon: {
    fontSize: '20px',
    color: '#ffffff',
  },
  arrowIconWhiteBg: {
    color: theme.palette.primary.main,
  }
});

// Componente de flecha personalizada (invisible, solo para que slick funcione)
const CustomArrow = () => null;

/**
 * GenericCarousel - Componente reutilizable de carousel
 * 
 * @param {Array} items - Array de items a mostrar en el carousel
 * @param {Function} renderItem - Función que recibe (item, index) y retorna el componente a renderizar
 * @param {Boolean} isWhiteBackground - Si el fondo es blanco (cambia color de dots)
 * @param {Object} customSettings - Configuración personalizada para el slider (opcional)
 */
class GenericCarousel extends React.Component {
  constructor(props) {
    super(props);
    this.sliderRef = React.createRef();
    this.state = {
      currentSlide: 0,
      isBeginning: true,
      isEnd: false,
    };
  }

  handlePrev = () => {
    this.sliderRef.current.slickPrev();
  };

  handleNext = () => {
    this.sliderRef.current.slickNext();
  };

  afterChange = (currentSlide) => {
    const { items } = this.props;
    const totalItems = items.length;
    const slidesToShow = totalItems >= 3 ? 3 : totalItems;
    
    this.setState({
      currentSlide,
      isBeginning: currentSlide === 0,
      isEnd: currentSlide >= totalItems - slidesToShow,
    });
  };

  render() {
    const { items, renderItem, isWhiteBackground, customSettings, classes } = this.props;
    const { isBeginning, isEnd } = this.state;
    
    const totalItems = items.length;
    const slidesToShow = totalItems >= 3 ? 3 : totalItems;
    const showArrows = totalItems > slidesToShow;

    const defaultSettings = {
      dots: false,
      infinite: false,
      speed: 500,
      slidesToShow: slidesToShow,
      slidesToScroll: totalItems >= 3 ? 3 : totalItems,
      arrows: true,
      nextArrow: <CustomArrow />,
      prevArrow: <CustomArrow />,
      dotsClass: 'slick-dots',
      autoplay: true,
      autoplaySpeed: 9000,
      pauseOnHover: true,
      pauseOnFocus: true,
      centerMode: false,
      afterChange: this.afterChange,
      responsive: [
        {
          breakpoint: 1280,
          settings: {
            dots: false,
            slidesToShow: totalItems >= 3 ? 3 : totalItems,
            slidesToScroll: totalItems >= 3 ? 3 : totalItems,
            infinite: false,
            centerMode: false,
          }
        },
        {
          breakpoint: 960,
          settings: {
            dots: false,
            slidesToShow: totalItems > 2 ? 2.25 : (totalItems >= 2 ? 2 : totalItems),
            slidesToScroll: totalItems > 2 ? 2 : (totalItems >= 2 ? 2 : totalItems),
            infinite: false,
            initialSlide: 0,
            centerMode: false,
            swipeToSlide: true,
            autoplay: false,
          }
        },
        {
          breakpoint: 600,
          settings: {
            dots: false,
            slidesToShow: 1.15,
            slidesToScroll: 1,
            infinite: false,
            initialSlide: 0,
            centerMode: false,
            swipeToSlide: true,
            autoplay: false,
          }
        },
        {
          breakpoint: 460,
          settings: {
            dots: false,
            slidesToShow: 1,
            slidesToScroll: 1,
            infinite: false,
            initialSlide: 0,
            centerMode: false,
            swipeToSlide: true,
            autoplay: false,
          }
        }
      ]
    };

    // Merge custom settings with default settings
    const settings = customSettings 
      ? { ...defaultSettings, ...customSettings }
      : defaultSettings;

    return (
      <>
        <div className={classNames(classes.carouselContainer, isWhiteBackground && classes.carouselContainerWhiteBg)}>
          <Slider ref={this.sliderRef} {...settings}>
            {items.map((item, index) => (
              <div key={index}>
                {renderItem(item, index)}
              </div>
            ))}
          </Slider>
        </div>
        
        {showArrows && (
          <div className={classes.customArrowsContainer}>
            <IconButton 
              className={classes.arrowButton}
              onClick={this.handlePrev}
              disabled={isBeginning}
              aria-label="anterior"
            >
              <ChevronLeftIcon 
                className={classNames(
                  classes.arrowIcon, 
                  isWhiteBackground && classes.arrowIconWhiteBg
                )} 
              />
            </IconButton>
            <IconButton 
              className={classes.arrowButton}
              onClick={this.handleNext}
              disabled={isEnd}
              aria-label="siguiente"
            >
              <ChevronRightIcon 
                className={classNames(
                  classes.arrowIcon, 
                  isWhiteBackground && classes.arrowIconWhiteBg
                )} 
              />
            </IconButton>
          </div>
        )}
      </>
    );
  }
}

GenericCarousel.propTypes = {
  classes: PropTypes.object.isRequired,
  items: PropTypes.array.isRequired,
  renderItem: PropTypes.func.isRequired,
  isWhiteBackground: PropTypes.bool,
  customSettings: PropTypes.object,
};

GenericCarousel.defaultProps = {
  isWhiteBackground: false,
  customSettings: null,
};

export default withStyles(styles)(GenericCarousel);